//
// .keyman-touch-layout JSON format
//
// Follows /common/schemas/keyman-touch-layout/keyman-touch-layout.spec.json for
// reading and
// /common/schemas/keyman-touch-layout/keyman-touch-layout.clean.spec.json for
// writing
//

export interface TouchLayoutFile {
  tablet?: TouchLayoutPlatform;
  phone?: TouchLayoutPlatform;
  desktop?: TouchLayoutPlatform;
};

export type TouchLayoutFont = string;
export type TouchLayoutFontSize = string;
export type TouchLayoutDefaultHint = "none"|"dot"|"longpress"|"multitap"|"flick"|"flick-n"|"flick-ne"|"flick-e"|"flick-se"|"flick-s"|"flick-sw"|"flick-w"|"flick-nw";

export interface TouchLayoutPlatform {
  font?: TouchLayoutFont;
  fontsize?: TouchLayoutFontSize;
  layer: TouchLayoutLayer[];
  displayUnderlying?: boolean;
  defaultHint: TouchLayoutDefaultHint;
};

export type TouchLayoutLayerId = string;  // pattern = /^[a-zA-Z0-9_-]+$/

export interface TouchLayoutLayer {
  id: TouchLayoutLayerId;
  row: TouchLayoutRow[];
};

export type TouchLayoutRowId = number;

export interface TouchLayoutRow {
  id: TouchLayoutRowId;
  key: TouchLayoutKey[];
};

type Key_Type = 'T'|'K'|'U'|'t'|'k'|'u';
type Key_Id = string;

export type TouchLayoutKeyId = `${Key_Type}_${Key_Id}`; // pattern = /^[TKUtku]_[a-zA-Z0-9_]+$/

/**
 * Denotes private-use identifiers that should be considered 'reserved'.
 */
export const PRIVATE_USE_IDS = [
  /**
   * Private-use identifiers used by KeymanWeb for the default multitap-into-caps-layer key
   * for keyboards with a caps layer while not defining multitaps on shift.
   */
  'T_*_MT_SHIFT_TO_SHIFT',
  'T_*_MT_SHIFT_TO_CAPS',
  'T_*_MT_SHIFT_TO_DEFAULT'
] as const;

/* A map of key field names with values matching the `typeof` the corresponding property
 * exists in common/web/keyboard-processor, keyboards/activeLayout.ts.
 *
 * Make sure that when one is updated, the other also is.  TS types are compile-time only,
 * so the run-time-accessible mapping in activeLayout.ts cannot be auto-generated by TS. */
export interface TouchLayoutKey {
  id?: TouchLayoutKeyId;
  text?: string;
  layer?: TouchLayoutLayerId;
  nextlayer?: TouchLayoutLayerId;
  font?: TouchLayoutFont;
  fontsize?: TouchLayoutFontSize;
  sp?: TouchLayoutKeySp;
  pad?: TouchLayoutKeyPad;
  width?: TouchLayoutKeyWidth;
  sk?: TouchLayoutSubKey[];
  flick?: TouchLayoutFlick;
  multitap?: TouchLayoutSubKey[];
  hint?: string;
};

export const enum TouchLayoutKeySp {
  normal=0,
  /** A 'frame' key, such as Shift or Enter, which is styled accordingly; uses
   *  the 'KeymanwebOsk' font on KeymanWeb */
  special=1,
  /** A 'frame' key, such as Shift or Enter, which is styled accordingly and is
   *  highlighted to indicate it is active, such as the shift key on a shift
   *  layer; uses the 'KeymanwebOsk' font on KeymanWeb */
  specialActive=2,
  /** **KeymanWeb runtime private use:** a variant of `special` with the
   *  keyboard font rather than 'KeymanwebOsk' font */
  customSpecial=3,
  /** **KeymanWeb runtime private use:** a  variant of `specialActive` with the
   *  keyboard font rather than 'KeymanwebOsk' font. */
  customSpecialActive=4,
  /** A styling signal to indicate that the key may have 'deadkey' type
   *  behaviour. */
  deadkey=8,
  /** A key which is rendered as a blank keycap, blocks any interaction */
  blank=9,
  /** Renders the key only as a gap or spacer, blocks any interaction */
  spacer=10
};

export type TouchLayoutKeyPad = number; // 0-100000
export type TouchLayoutKeyWidth = number; // 0-100000

export interface TouchLayoutSubKey {
  id: TouchLayoutKeyId;
  text?: string;
  layer?: TouchLayoutLayerId;
  nextlayer?: TouchLayoutLayerId;
  font?: TouchLayoutFont;
  fontsize?: TouchLayoutFontSize;
  sp?: TouchLayoutKeySp;
  pad?: TouchLayoutKeyPad;
  width?: TouchLayoutKeyWidth;
  default?: boolean;  // Only used for longpress currently
};

export interface TouchLayoutFlick {
  n?: TouchLayoutSubKey;
  s?: TouchLayoutSubKey;
  e?: TouchLayoutSubKey;
  w?: TouchLayoutSubKey;
  ne?: TouchLayoutSubKey;
  nw?: TouchLayoutSubKey;
  se?: TouchLayoutSubKey;
  sw?: TouchLayoutSubKey;
};
